/**
 * Cover art for a collection — the image the storefront tile renders.
 *
 * Reuses `storeCatalog.requestImageUpload`: the presigned key is scoped to the
 * **company**, not to a listing, so it already works for an entity that isn't a
 * product. Same no-confirm contract as product images — the record IS the URL,
 * persisted when the form is saved.
 */
import { useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { Button, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = ["image/jpeg", "image/png", "image/webp"];

export function CollectionCoverField() {
  const form = useFormContext<{ imageUrl: string }>();
  const value = form.watch("imageUrl");
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const requestUpload = trpc.storeCatalog.requestImageUpload.useMutation();

  async function onPick(file: File) {
    if (!ACCEPT.includes(file.type)) {
      toast.error("Unsupported file", { description: "Use a JPEG, PNG or WebP image." });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image too large", { description: "Cover art must be 5 MB or smaller." });
      return;
    }
    setBusy(true);
    try {
      const { uploadUrl, publicUrl } = await requestUpload.mutateAsync({
        contentType: file.type as "image/jpeg" | "image/png" | "image/webp",
        size: file.size,
      });
      const res = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "content-type": file.type },
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      form.setValue("imageUrl", publicUrl ?? "", { shouldDirty: true });
    } catch (err) {
      toast.error("Couldn't upload the cover", { description: storeErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-1.5 text-sm font-medium leading-[18px] text-ink-2">Cover image</div>
      <div className="flex items-center gap-3">
        {value ? (
          <img
            src={value}
            alt=""
            className="h-20 w-20 shrink-0 rounded border border-line object-cover"
          />
        ) : (
          <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded border border-dashed border-line-2 bg-raised text-muted">
            <ImagePlus className="h-5 w-5" aria-hidden />
          </span>
        )}
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              intent="secondary"
              size="sm"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
              {value ? "Replace" : "Upload"}
            </Button>
            {value ? (
              <Button
                intent="secondary"
                size="sm"
                disabled={busy}
                onClick={() => form.setValue("imageUrl", "", { shouldDirty: true })}
              >
                <Trash2 size={14} aria-hidden />
                Remove
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-muted">
            Shown on the storefront collection tile. JPEG, PNG or WebP, up to 5 MB.
          </p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT.join(",")}
        className="hidden"
        aria-label="Upload cover image"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so re-picking the same file still fires `change`.
          e.target.value = "";
          if (file) void onPick(file);
        }}
      />
    </div>
  );
}
